// The treatment card, browser-side: the palette the builder offers, and the
// one definition of how a completed card's values are laid out.
//
// The RULES live on the server — _shared/treatment-card.ts owns which blocks a
// card may use, what its note says, and which values carry forward from the
// last session. Nothing here re-derives any of them: the builder offers a
// palette the API will accept, the dialog renders what it is given, and the
// carry-forward arrives from the API as `prefill`. Keep it that way. A second
// copy of "which value survives a card change" is a second answer to what was
// delivered to somebody.

import type { IntakeAnswers, IntakeBlock, MultiSelectAnswer } from '@/lib/api'

/**
 * The blocks a treatment card may use, in palette order.
 *
 * `consent` is absent, and that is enforced by the API rather than remembered
 * here: nobody consents to a record of what was done to them. Consent is asked
 * for on the intake form, by the client, before the session.
 */
export const CARD_PALETTE = [
  'number',
  'text',
  'textarea',
  'select',
  'multi_select',
  'yes_no',
  'heading',
  'paragraph',
] as const

export type CardBlockType = typeof CARD_PALETTE[number]

export const CARD_BLOCK_LABELS: Record<CardBlockType, string> = {
  number: 'Number',
  text: 'Short answer',
  textarea: 'Long answer',
  select: 'Multiple choice',
  multi_select: 'Tick all that apply',
  yes_no: 'Yes / no',
  heading: 'Heading',
  paragraph: 'Paragraph',
}

export function cardBlockHasOptions(type: string): boolean {
  return type === 'select' || type === 'multi_select'
}

export function isCardQuestion(type: string): boolean {
  return type !== 'heading' && type !== 'paragraph'
}

let nextBlockSeq = 1
export function newCardBlock(type: CardBlockType): IntakeBlock {
  const block: IntakeBlock = { id: `b-${Date.now()}-${nextBlockSeq++}`, type, label: '' }
  if (cardBlockHasOptions(type)) block.options = ['', '']
  return block
}

// ── Reading a completed card ─────────────────────────────────────────────────

export interface CardLine {
  block_id: string
  label: string
  value: string
}

function isAnswered(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') {
    const selected = (value as Partial<MultiSelectAnswer>).selected
    return Array.isArray(selected) && selected.length > 0
  }
  return true
}

/** One field's value as a person reads it. Mirrors formatAnswer on the server. */
export function formatCardAnswer(block: IntakeBlock, value: unknown): string {
  switch (block.type) {
    case 'yes_no':
      return value === 'yes' ? 'Yes' : 'No'
    case 'number': {
      const num = typeof value === 'number' ? value : Number(value)
      const shown = Number.isFinite(num) ? String(num) : String(value)
      return block.unit ? `${shown} ${block.unit}` : shown
    }
    case 'multi_select': {
      const v = value as Partial<MultiSelectAnswer>
      const picked = (v?.selected ?? []).join(', ')
      return v?.note ? `${picked} — ${v.note}` : picked
    }
    default:
      return String(value)
  }
}

/**
 * The answered fields of a completed card, in the order the card asks them.
 *
 * Always read against the note's OWN snapshot, never the card's current
 * document — a card edited since is a card that asked something else, and
 * relabelling an old session's values would misreport what was delivered.
 * Unanswered fields are omitted, matching the server's render.
 */
export function cardLines(
  snapshot: { blocks: IntakeBlock[] } | null | undefined,
  answers: IntakeAnswers | null | undefined,
): CardLine[] {
  if (!snapshot?.blocks || !answers) return []
  const lines: CardLine[] = []
  for (const block of snapshot.blocks) {
    if (!isCardQuestion(block.type)) continue
    const value = answers[block.id]
    if (!isAnswered(value)) continue
    lines.push({ block_id: block.id, label: block.label, value: formatCardAnswer(block, value) })
  }
  return lines
}

/** A one-line summary for a place with no room for the fields — the booking sheet. */
export function cardSummary(
  snapshot: { blocks: IntakeBlock[] } | null | undefined,
  answers: IntakeAnswers | null | undefined,
  max = 3,
): string {
  const lines = cardLines(snapshot, answers)
  if (lines.length === 0) return ''
  const shown = lines.slice(0, max).map((l) => `${l.label} ${l.value}`).join(' · ')
  // The count is stated rather than an ellipsis: "and 4 more" says how much is
  // not on screen, which "…" does not.
  return lines.length > max ? `${shown} · and ${lines.length - max} more` : shown
}
